import React from 'react';
import AccountAvatarTrigger from '../avatars/AccountAvatarTrigger';
import { ResponseStatusAvatar } from '../../workAvailability/ResponseStatusProvider';
import { getArtistFallbackAvatarStyle } from './groupParticipantsPanelConfigUtils';

export function MemberPartScopeTabs({
  tabs = [],
  activeValue,
  onChange,
  wrapperClassName,
  tabClassName,
  keyPrefix = 'member-scope',
  ariaLabel = '出演者の公演切り替え',
}) {
  if (!Array.isArray(tabs) || tabs.length === 0) return null;
  return (
    <div className={wrapperClassName} role="tablist" aria-label={ariaLabel}>
      {tabs.map((tab) => {
        const active = String(activeValue) === String(tab.eventUuid);
        return (
          <button
            key={`${keyPrefix}-${tab.eventUuid}`}
            type="button"
            className={`${tabClassName}${active ? ' is-active' : ''}`}
            onClick={() => onChange?.(tab.eventUuid)}
            role="tab"
            aria-selected={active}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export function MemberRoleBadges({
  member,
  badgesClassName,
  concertmasterBadgeClassName,
  chiefBadgeClassName,
  roleBadgeClassName,
  includeCustomRoles = true,
}) {
  if (!member) return null;
  const hasCustomRoles = includeCustomRoles && Array.isArray(member.customRoles) && member.customRoles.length > 0;
  if (!member.isConcertmaster && !member.isChief && !hasCustomRoles) return null;
  return (
    <div className={badgesClassName}>
      {member.isConcertmaster ? (
        <span className={concertmasterBadgeClassName}>コンサートマスター</span>
      ) : null}
      {member.isChief ? <span className={chiefBadgeClassName}>首席</span> : null}
      {hasCustomRoles
        ? member.customRoles.map((role) => (
            <span
              key={`${role?.id || role?.name || 'role'}-member`}
              className={roleBadgeClassName}
            >
              {role?.name || role?.role_name || '役職'}
            </span>
          ))
        : null}
    </div>
  );
}

export function buildMemberRoleText(member, sectionLabel = '') {
  if (!member) return '';
  const normalizedSection = String(sectionLabel || '').trim();
  const labels = [
    member.isConcertmaster ? 'コンサートマスター' : '',
    member.isChief ? '首席' : '',
    ...(Array.isArray(member.customRoles)
      ? member.customRoles.map((role) => role?.name || role?.role_name || '')
      : []),
  ]
    .map((label) => String(label || '').trim())
    .filter((label) => label && label !== normalizedSection);
  return Array.from(new Set(labels)).join(' / ');
}

export function MemberArtistRow({
  member,
  onClick,
  avatarStyle,
  avatarInitial,
  rowClassName,
  avatarClassName,
  infoClassName,
  nameClassName,
  membershipClassName,
  badgesClassName,
  concertmasterBadgeClassName,
  chiefBadgeClassName,
  roleBadgeClassName,
  showMembership = true,
  showBadges = true,
  showRolesAsText = false,
  sectionLabel = '',
  includeCustomRoles = true,
  renderTrailing,
}) {
  const clickable = typeof onClick === 'function';
  const hasCustomRoles = includeCustomRoles && Array.isArray(member?.customRoles) && member.customRoles.length > 0;
  const hasBadges =
    showBadges &&
    (member?.isConcertmaster || member?.isChief || hasCustomRoles);
  const hasMembership = showMembership && !!member?.eventMembershipLabel;
  const roleText = showRolesAsText ? buildMemberRoleText(member, sectionLabel) : '';
  const hasAvatarImage = !!(member?.pic || member?.avatarUrl);
  const resolvedAvatarStyle =
    (member?.avatarUrl
      ? {
          backgroundImage: `url(${member.avatarUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      : avatarStyle?.(member?.pic, member?.gender)) ||
    (!hasAvatarImage ? getArtistFallbackAvatarStyle(member?.gender) : undefined);
  const resolvedInfoClassName = [
    infoClassName,
    !hasBadges && !hasMembership && !roleText ? 'member-modal__info--single-line' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <li
      className={rowClassName}
      onClick={clickable ? () => onClick(member) : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick(member);
              }
            }
          : undefined
      }
    >
      <AccountAvatarTrigger
        className={avatarClassName}
        style={resolvedAvatarStyle}
        fallback={!hasAvatarImage && avatarInitial?.(member.name)}
        onClick={clickable ? () => onClick(member) : undefined}
        stopPropagation
      />
      <div className={resolvedInfoClassName}>
        {showBadges ? (
          <MemberRoleBadges
            member={member}
            badgesClassName={badgesClassName}
            concertmasterBadgeClassName={concertmasterBadgeClassName}
            chiefBadgeClassName={chiefBadgeClassName}
            roleBadgeClassName={roleBadgeClassName}
            includeCustomRoles={includeCustomRoles}
          />
        ) : null}
        <div className={nameClassName}>{member.name}</div>
        {roleText ? <div className={membershipClassName}>{roleText}</div> : null}
        {hasMembership ? (
          <div className={membershipClassName}>{member.eventMembershipLabel}</div>
        ) : null}
        {member?.eventPartLabel ? (
          <div className={`${membershipClassName} member-modal__event-part-note`}>
            {member.eventPartLabel}
          </div>
        ) : null}
      </div>
      {typeof renderTrailing === 'function' ? renderTrailing(member) : null}
    </li>
  );
}

export function MemberArtistSection({
  section,
  onArtistClick,
  avatarStyle,
  avatarInitial,
  sectionClassName,
  titleClassName,
  listClassName,
  rowClassName,
  avatarClassName,
  infoClassName,
  nameClassName,
  membershipClassName,
  badgesClassName,
  concertmasterBadgeClassName,
  chiefBadgeClassName,
  roleBadgeClassName,
  showMembership = true,
  showBadges = true,
  showRolesAsText = false,
  includeCustomRoles = true,
  renderTrailing,
}) {
  if (!section || !Array.isArray(section.members)) return null;
  return (
    <section className={sectionClassName}>
      {(section.titleShort || section.title) ? (
        <h4 className={titleClassName}>
          {section.titleShort || section.title}
        </h4>
      ) : null}
      <ul className={listClassName}>
        {section.members.map((member) => (
          <MemberArtistRow
            key={member.id}
            member={member}
            onClick={onArtistClick}
            avatarStyle={avatarStyle}
            avatarInitial={avatarInitial}
            rowClassName={rowClassName}
            avatarClassName={avatarClassName}
            infoClassName={infoClassName}
            nameClassName={nameClassName}
            membershipClassName={membershipClassName}
            badgesClassName={badgesClassName}
            concertmasterBadgeClassName={concertmasterBadgeClassName}
            chiefBadgeClassName={chiefBadgeClassName}
            roleBadgeClassName={roleBadgeClassName}
            showMembership={showMembership}
            showBadges={showBadges}
            showRolesAsText={showRolesAsText}
            sectionLabel={section.titleShort || section.title}
            includeCustomRoles={includeCustomRoles}
            renderTrailing={renderTrailing}
          />
        ))}
      </ul>
    </section>
  );
}

export function MemberPersonSection({
  title,
  people = [],
  onClick,
  getKey,
  getName,
  getMeta,
  getMembership,
  getAvatarImage,
  getAdminUserId,
  avatarStyle,
  avatarInitial,
  sectionClassName,
  titleClassName,
  listClassName,
  rowClassName,
  avatarClassName,
  textClassName,
  nameClassName,
  metaClassName,
  membershipClassName,
  emptyClassName,
  emptyText = '未設定',
}) {
  const list = Array.isArray(people) ? people : [];
  return (
    <section className={sectionClassName}>
      <h4 className={titleClassName}>{title}</h4>
      {list.length ? (
        <ul className={listClassName}>
          {list.map((person, index) => {
            const displayName = getName?.(person, index) || '';
            const image = getAvatarImage?.(person, index) || '';
            const meta = getMeta?.(person, index) || '';
            const membership = getMembership?.(person, index) || '';
            return (
              <li
                key={getKey?.(person, index) || `${displayName}-${index}`}
                className={rowClassName}
                onClick={() => onClick?.(person, index)}
                role={typeof onClick === 'function' ? 'button' : undefined}
                tabIndex={typeof onClick === 'function' ? 0 : undefined}
                onKeyDown={
                  typeof onClick === 'function'
                    ? (event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        onClick(person, index);
                      }
                    : undefined
                }
              >
                <ResponseStatusAvatar adminUserId={getAdminUserId?.(person, index)} badgePlacement="avatar">
                <AccountAvatarTrigger
                  className={avatarClassName}
                  style={avatarStyle?.(image, person.gender)}
                  fallback={!image && avatarInitial?.(displayName)}
                  onClick={typeof onClick === 'function' ? () => onClick(person, index) : undefined}
                  stopPropagation
                />
                </ResponseStatusAvatar>
                <div className={textClassName}>
                  <div className={nameClassName}>{displayName}</div>
                  {meta ? <div className={metaClassName}>{meta}</div> : null}
                  {membership ? <div className={membershipClassName}>{membership}</div> : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className={emptyClassName}>{emptyText}</div>
      )}
    </section>
  );
}
